import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment-timezone';
import isNil from 'lodash/isNil';
import get from 'lodash/get';
import { compose } from 'recompose';

import AppointmentForm from './appointmentForm';
import { t } from '../../../../modules/infra/i18n';
import SaveButton from '../../../common/buttons/saveButton';
import PaymentDetails from './paymentDetails/paymentDetails';
import CancelAppointment from './cancelAppointment';
import SelectStatus from './selectStatus';
import ChargeContainer from './containers/GoToRegisterButtons';
import WaiverButton from './containers/WaiverButton';
import ConfirmationScreen from './confirmationScreen/confirmationScreen';
import {
  isPaid,
  isCharged,
  isDepositOrPrepaid,
  isWithinTippingWindow,
  isSquireAppointment,
  getGiftCardId,
  isAppointmentFullyRefunded,
  isAppointmentPartiallyRefunded,
  isAppointmentRefunded,
} from '../../../common/helpers/appointments';
import getPaymentDetailsData from './paymentDetails/functions';
import { APPOINTMENT_STATUS } from '../constants';
import {
  canUserModifyAppointments,
  canUserCancelAppointments,
  canUserViewRegister,
} from '../../../common/helpers/users';
import withPermissions from '../../../auth/utils/withPermissions';
import AddProducts from './containers/AddProducts';
import withTimezone from '../../../auth/utils/withTimezone';
import AddTip from './containers/AddTip';
import MessageClientButton from './MessageClientButton';
import { RebookAppointmentButton } from './rebookAppointmentButton';

class EditAppointmentForm extends AppointmentForm {
  constructor(props) {
    super(props);

    this.edit = true;
  }

  shouldRenderSelectStatus() {
    const { appointment, timezone } = this.props;
    const isBeforeToday = moment(appointment.start).tz(timezone).startOf('day').isBefore(moment());

    return (appointment.status !== APPOINTMENT_STATUS.canceled && isBeforeToday);
  }

  renderSelectStatus() {
    const { selectedStatus, noShow, paidInShop, handlers, barberReady, appointment } = this.props;
    if (!this.shouldRenderSelectStatus) return null;

    const canModifyAppointments = canUserModifyAppointments(this.props.permissions);

    return (
      <SelectStatus
        disabled={!canModifyAppointments || this.appointmentCanceled()}
        selectedStatus={selectedStatus}
        barberId={appointment.barberId}
        noShow={noShow}
        paidInShop={paidInShop}
        shouldRenderPaidInShop={this.shouldPayInShopOrCharge()}
        toggleArrived={handlers.toggleArrived}
        toggleNoShow={handlers.toggleNoShow}
        togglePaidInShop={handlers.togglePaidInShop}
        barberReady={barberReady}
        toggleBarberReady={handlers.toggleBarberReady}
      />
    );
  }

  shouldPayInShopOrCharge() {
    if (this.appointmentCanceled()) return false;

    const { appointment } = this.props;
    const isPaymentSuccessful = get(appointment, 'payment.successful');

    return !(isSquireAppointment(appointment) && isPaymentSuccessful) && !isPaid(this.props.appointment);
  }

  isTodayOrAfter() {
    const { appointment, timezone } = this.props;
    return moment(appointment.start)
      .tz(timezone).startOf('day')
      .isSameOrAfter(moment().tz(timezone).startOf('day'));
  }

  shouldRenderAddProducts() {
    return (
      isPaid(this.props.appointment) &&
      isSquireAppointment(this.props.appointment) &&
      this.isTodayOrAfter() &&
      !this.appointmentCanceled()
    );
  }

  shouldRenderAddTip() {
    // squire appointment after authorization and before capture
    const { appointment, permissions } = this.props;
    const isDepositWithinTippingWindow = isDepositOrPrepaid(appointment) && isWithinTippingWindow(appointment);
    return isSquireAppointment(appointment)
      && isPaid(appointment)
      && (!isCharged(appointment) || isDepositWithinTippingWindow)
      && canUserViewRegister(permissions)
      && !this.appointmentCanceled();
  }

  getTotalCost() {
    const paymentData = this.getPaymentData();
    return isNil(paymentData.discountedTotal) ? paymentData.total : paymentData.discountedTotal;
  }

  appointmentCanceled() {
    return this.props.selectedStatus === APPOINTMENT_STATUS.canceled;
  }

  renderCharge() {
    if (!this.shouldPayInShopOrCharge()) return null;

    const totalCost = this.getTotalCost();
    const showGoToProducts = this.isTodayOrAfter();

    return (<ChargeContainer
      appointment={this.props.appointment}
      areChangesPending={this.props.areChangesPending}
      updateAppointment={this.props.handlers.save}
      totalCost={totalCost}
      showGoToProducts={showGoToProducts}
    />);
  }

  renderAddProducts() {
    if (!this.shouldRenderAddProducts()) return null;

    return (
      <AddProducts appointment={this.props.appointment} />
    );
  }

  renderAddTip() {
    if (!this.shouldRenderAddTip()) return null;

    return (
      <AddTip
        appointment={this.props.appointment}
        areChangesPending={this.props.areChangesPending}
        updateAppointment={this.props.handlers.save}
      />
    );
  }

  renderWaiver() {
    return <WaiverButton appointment={this.props.appointment} />;
  }

  renderCustomerAction() {
    return <MessageClientButton appointment={this.props.appointment} onClose={this.props.onClose} />;
  }

  shouldRenderRefund() {
    return isCharged(this.props.appointment) && !this.props.appointment.payment.chargeRefundId;
  }

  shouldRenderPaymentInfo() {
    const { selectedServices } = this.props;
    return selectedServices.length > 0;
  }

  getPaymentData() {
    const {
      selectedServices,
      appointment,
      promoAmount,
    } = this.props;

    return getPaymentDetailsData({
      selectedServices,
      tip: appointment.tipAmount,
      discount: appointment.discountAmount,
      promoAmount,
      isRefunded: isAppointmentRefunded(appointment),
      refundedAmount: appointment.refundedAmount,
    });
  }

  renderPaymentInfo() {
    if (!this.shouldRenderPaymentInfo()) return null;

    const {
      appointment,
      paidInShop,
      allowMultipleServices,
    } = this.props;

    const dataForStatus = {
      status: appointment.status,
      paymentStatus: appointment.paymentStatus,
      paidInShop,
      canceledBy: appointment.canceledBy,
      allowMultipleServices,
      payment: appointment.payment,
      appointmentServices: appointment.appointment_service,
      giftCardId: getGiftCardId(appointment),
      isPartiallyRefunded: isAppointmentPartiallyRefunded(appointment),
      isFullyRefunded: isAppointmentFullyRefunded(appointment),
    };

    return (
      <PaymentDetails
        paymentDetailsData={this.getPaymentData()}
        dataForStatus={dataForStatus}
      />
    );
  }

  renderActionControls() {
    const {
      appointment,
      isRecurring,
      saveButtonDisabled,
      permissions,
    } = this.props;

    let actions = [];
    if (!this.appointmentCanceled()) {
      if (isRecurring && canUserCancelAppointments(permissions)) {
        actions.push(<CancelAppointment appointmentId={appointment.id} cancelAll key={1} />);
      }
      if (appointment.canBeCancelled) {
        actions.push(<CancelAppointment appointmentId={appointment.id} key={2} />);
      }
    }

    actions.push(<RebookAppointmentButton appointment={appointment} />);

    if (canUserModifyAppointments(permissions)) {
      actions.push(
        <SaveButton content={t('appointment:form.save_button')} disabled={saveButtonDisabled} display-if={!this.appointmentCanceled()} />
      );
    }

    return (
      <div>{ actions.length ? actions : null }</div>
    );
  }

  renderConfirmationScreen() {
    const { confirmationScreenShowed } = this.props;

    if (!confirmationScreenShowed) return null;

    const {
      selectedBarber,
      currentBarber,
      selectedServices,
      selectedCustomer,
      newCustomerDetails,
      selectedDuration,
      day,
      time,
      saveButtonDisabled,
      sendNotifications,
      autoChargeRecurring,
      tipAmount,
      selectedIsRecurring,
      recurrEveryNumDays,
      onClose,
    } = this.props;

    const {
      appointment,
      timezone,
      handlers,
      currency,
    } = this.props;

    return (
      <ConfirmationScreen
        selectedBarber={selectedBarber}
        currentBarber={currentBarber}
        selectedServices={selectedServices}
        selectedDuration={selectedDuration}
        selectedCustomer={selectedCustomer || newCustomerDetails}
        day={day}
        time={time}
        saveButtonDisabled={saveButtonDisabled}
        appointment={appointment}
        timezone={timezone}
        save={handlers.save}
        saveAll={handlers.saveAll}
        hideConfirmationScreen={handlers.hideConfirmationScreen}
        saveWithoutConfirmation={handlers.saveWithoutConfirmation}
        toggleNotifications={handlers.toggleNotifications}
        sendNotifications={sendNotifications}
        autoChargeRecurring={autoChargeRecurring}
        isRecurring={selectedIsRecurring}
        recurrEveryNumDays={recurrEveryNumDays}
        tipAmount={tipAmount}
        currency={currency}
        onClose={onClose}
      />
    );
  }
}

EditAppointmentForm.propTypes = {
  appointment: PropTypes.object,
  timezone: PropTypes.string,
  selectedStatus: PropTypes.number.isRequired,
  paidInShop: PropTypes.bool.isRequired,
  noShow: PropTypes.bool.isRequired,
  confirmationScreenShowed: PropTypes.bool.isRequired,
  sendNotifications: PropTypes.bool.isRequired,
  permissions: PropTypes.object.isRequired,
  areChangesPending: PropTypes.bool,
};

export default compose(
  withPermissions,
  withTimezone,
)(EditAppointmentForm);
